/**
 * Boot instrumentation (boot LCP plan, P4).
 *
 * `performance.mark()` only — no measures, no telemetry, nothing shipped to a
 * backend. The marks exist so the ~1.1s idle window between the Firebase
 * `accounts:lookup` finishing and the API burst starting can be attributed from
 * the Performance panel / `performance.getEntriesByType("mark")` in a lab run,
 * instead of guessed at.
 *
 * Names are all prefixed `iq:boot:` so a run can be filtered in one pass:
 *
 * ```js
 * performance.getEntriesByType("mark")
 *   .filter((m) => m.name.startsWith("iq:boot:"))
 *   .map((m) => [m.name, Math.round(m.startTime)]);
 * ```
 */

/** Every boot mark this app emits, so the names stay typo-proof and greppable. */
export const BOOT_MARKS = {
  APP_MOUNT: "iq:boot:app-mount",
  SPLASH_FINISHED: "iq:boot:splash-finished",
  AUTH_INITIALIZED: "iq:boot:auth-initialized",
  AUTH_CONTEXT_USER: "iq:boot:auth-context-user",
  USER_DETAILS_RESOLVED: "iq:boot:user-details-resolved",
} as const;

export type BootMark = (typeof BOOT_MARKS)[keyof typeof BOOT_MARKS];

/**
 * Record a boot mark. A no-op wherever the Performance API is missing (jsdom in
 * some setups, very old browsers), and never throws — instrumentation must not
 * be able to break boot.
 */
export function markBoot(name: BootMark): void {
  if (typeof performance === "undefined" || typeof performance.mark !== "function") return;
  try {
    performance.mark(name);
  } catch {
    // Marks are diagnostics; a failure here is never worth surfacing.
  }
}
